import Clarity from "@microsoft/clarity";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";

interface ClarityModule {
	init(id: string): void;
	consentV2(consentOptions: { ad_Storage: "granted" | "denied"; analytics_Storage: "granted" | "denied" }): void;
	event(name: string): void;
	setTag(key: string, value: string): void;
	upgrade(reason: string): void;
}

export type ClarityEventName =
	| "addToCart"
	| "cartStepPaymentDelivery"
	| "cartStepAddress"
	| "orderSubmitFailed"
	| "paymentGateOpened"
	| "orderCompleted"
	| "paymentPending"
	| "paymentFailed"
	| "subscriptionCompleted"
	| "subscriptionConfiguratorOpened"
	| "subscriptionConfiguratorAbandoned"
	| "subscriptionConfiguratorCompleted"
	| "subscriptionConfiguratorBack"
	| `subscriptionConfiguratorStep${number}`
	| "hpHeroCtaShop"
	| "hpHeroCtaChristmas"
	| "hpHeroCtaSubscription"
	| "hpIntroSubscriptionCtaBuild"
	| "hpIntroSubscriptionCtaInfo"
	| "hpIntroGiftCtaBuild"
	| "hpIntroGiftCtaInfo"
	| "hpIntroChristmasCta";

export type ClarityTagKey =
	| "template"
	| "locale"
	| "pricingLevel"
	| "loggedIn"
	| "familyClubMember"
	| "currency"
	| "destinationCountry"
	| "subscriptionKind";

export type ClarityUpgradeReason = "checkout" | "subscriptionConfigurator";

let active: ClarityModule | undefined;
const pendingTags = new Map<ClarityTagKey, string>();
const pendingEvents: ClarityEventName[] = [];
let pendingUpgradeReason: ClarityUpgradeReason | undefined;
/** Only a guard for the case when consent never resolves and the queue is never flushed. */
const maxPendingEvents = 20;

function clearPending(): void {
	pendingTags.clear();
	pendingEvents.length = 0;
	pendingUpgradeReason = undefined;
}

export function initClarity(
	settings: Pick<GlobalSettings, "CLARITY" | "CLARITY_ENABLED">,
	module: ClarityModule = Clarity,
): void {
	active = undefined;
	if (!settings.CLARITY_ENABLED || !settings.CLARITY) {
		clearPending();
		return;
	}
	module.init(settings.CLARITY);
	active = module;
	for (const [key, value] of pendingTags) {
		module.setTag(key, value);
	}
	if (pendingUpgradeReason !== undefined) {
		module.upgrade(pendingUpgradeReason);
	}
	for (const name of pendingEvents) {
		module.event(name);
	}
	clearPending();
}

export function clarityConsent(granted: { analytics: boolean; ads: boolean }): void {
	active?.consentV2({
		analytics_Storage: granted.analytics ? "granted" : "denied",
		ad_Storage: granted.ads ? "granted" : "denied",
	});
}

export function clarityTag(key: ClarityTagKey, value: string): void {
	if (!active) {
		pendingTags.set(key, value);
		return;
	}
	active.setTag(key, value);
}

export function clarityEvent(name: ClarityEventName): void {
	if (!active) {
		// Pages report their step on mount, which happens before consent resolves and Clarity initializes.
		if (pendingEvents.length < maxPendingEvents) {
			pendingEvents.push(name);
		}
		return;
	}
	active.event(name);
}

export function clarityUpgrade(reason: ClarityUpgradeReason): void {
	if (!active) {
		pendingUpgradeReason ??= reason;
		return;
	}
	active.upgrade(reason);
}
